import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { ArrowLeft, Calendar, Loader2 } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import { Footer } from '../components/site/FooterCTA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

marked.setOptions({ gfm: true, breaks: false });

// SEC-002: sanitize markdown-rendered HTML with a strict allowlist so a
// malicious admin post cannot execute scripts on the primary origin.
const SAFE_TAGS = [
  'a', 'p', 'br', 'hr', 'strong', 'em', 'b', 'i', 'u', 's', 'del', 'mark',
  'blockquote', 'code', 'pre', 'kbd', 'samp',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'figure', 'figcaption',
];
const SAFE_ATTRS = ['href', 'title', 'alt', 'src', 'name', 'id', 'target', 'rel', 'colspan', 'rowspan'];

const renderSafeMarkdown = (body) => {
  const rawHtml = marked.parse(body || '');
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: SAFE_TAGS,
    ALLOWED_ATTR: SAFE_ATTRS,
    // Strip anything that could execute or leak: on*, javascript:, data:, srcset, etc.
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick'],
    ALLOW_DATA_ATTR: false,
    // Only allow http(s) / mailto links.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
};

const upsertMeta = (attr, name, content) => {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(res.data);
        setStatus('ready');
      } catch (e) {
        if (e?.response?.status === 404) setStatus('not_found');
        else setStatus('error');
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const url = `${window.location.origin}/blog/${post.slug}`;
    document.title = `${post.title} — YouBuild`;
    upsertMeta('name', 'description', post.excerpt);
    upsertMeta('property', 'og:title', post.title);
    upsertMeta('property', 'og:description', post.excerpt);
    upsertMeta('property', 'og:type', 'article');
    upsertMeta('property', 'og:url', url);
    if (post.cover_image_url) upsertMeta('property', 'og:image', post.cover_image_url);
    upsertMeta('name', 'twitter:card', post.cover_image_url ? 'summary_large_image' : 'summary');
  }, [post]);

  return (
    <div className="min-h-screen font-sans antialiased" style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}>
      <Navbar />
      <main>
        <article className="pt-32 pb-24 lg:pt-40 lg:pb-32">
          <div className="max-w-[760px] mx-auto px-6 lg:px-10">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13.5px] mb-8 transition-opacity hover:opacity-80" style={{ color: 'var(--yb-indigo)' }}>
              <ArrowLeft size={14} /> All posts
            </Link>

            {status === 'loading' && (
              <div className="flex items-center justify-center py-24" style={{ color: 'var(--yb-muted)' }}>
                <Loader2 className="animate-spin" />
              </div>
            )}

            {status === 'not_found' && (
              <div className="py-16">
                <h1 className="font-serif text-4xl md:text-5xl" style={{ color: 'var(--yb-ink)' }}>
                  Post not found
                </h1>
                <p className="mt-4" style={{ color: 'var(--yb-muted)' }}>
                  This post may have been unpublished or removed.
                </p>
              </div>
            )}

            {status === 'error' && (
              <p style={{ color: '#B4530A' }}>Could not load the post. Please try again.</p>
            )}

            {status === 'ready' && post && (
              <>
                <div className="flex items-center gap-2 text-[13px] mb-5" style={{ color: 'var(--yb-muted)' }}>
                  <Calendar size={13} />
                  <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
                </div>
                <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em]" style={{ color: 'var(--yb-ink)' }}>
                  {post.title}
                </h1>
                <p className="mt-5 text-[17px] leading-relaxed" style={{ color: 'var(--yb-muted)' }}>
                  {post.excerpt}
                </p>

                {post.cover_image_url && (
                  <div className="mt-10 rounded-2xl overflow-hidden border" style={{ borderColor: 'var(--yb-border)' }}>
                    <img src={post.cover_image_url} alt={post.title} className="w-full" />
                  </div>
                )}

                <div
                  className="yb-prose mt-10"
                  dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(post.body) }}
                />
              </>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
